import { XMarkIcon } from '@heroicons/react/24/outline'
import * as _ from 'lodash-es'
import { observer } from 'mobx-react'
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Helmet } from 'react-helmet'
import { useLocation, useNavigate, Link } from 'react-router-dom'

import UserIcon from '@/assets/images/user_icon.svg'
import { MultiSelectDropdown } from '@/components/forms/MultiSelectDropdown'
import { TextInput } from '@/components/forms/TextInput'
import { DownloadIcon, SearchIcon } from '@/components/icons'
import { DataTable, getSortProps } from '@/components/shared/DataTable'
import { Highlights } from '@/components/shared/Highlights'
import { Button } from '@/components/ui/Button'
import { Modal } from '@/components/ui/Modal'
import useAbortControllers from '@/hooks/AbortControllers'
import useHoverItem from '@/hooks/HoverItem'
import {
  createDealerExport,
  getDealerExport,
  getDealerKpis,
  getDealers,
  getFilterListOptions,
} from '@/services/dealers.service'
import { logout, updateUser } from '@/services/user.service'
import { DashboardStoreContext } from '@/stores/DashboardStore'
import { RootStoreContext } from '@/stores/RootStore'
import { TaskStoreContext } from '@/stores/TaskStore'
import { UserStoreContext } from '@/stores/UserStore'
import { track } from '@/utils/analytics'
import { EXPORT_INITIATED, SEARCH_PERFORMED, SORT_CHANGED } from '@/utils/analytics.constants'
import { handlePagination, toast } from '@/utils/helpers'

import DealerNameCell from './DealerNameCell'

const DEALER_BASE_URL = '/dealers/?expand=group,manufacturer,ro_metrics'
const KPI_BASE_URL = '/dealers/kpis'
const DEALER_GROUP_BASE_URL = '/dealers/dealer-groups/'
const MANUFACTURER_BASE_URL = '/dealers/manufacturers/'
const ADMIN_BASE_URL = '/dealers/admins/'
const MANAGER_BASE_URL = '/dealers/managers/'
const ASSISTANT_BASE_URL = '/dealers/assistant-managers/'
const DMS_BASE_URL = '/dealers/dealer-management-systems/'
const isDmsEnabled = import.meta.env.VITE_DMS_ENABLED === 'true'

/**
 *
 * Dashboard
 *
 */
const Dashboard = observer(() => {
  // Context
  const navigate = useNavigate()
  const location = useLocation()
  const {
    filters,
    resetFilters,
    selectedFilters,
    sortedColumn,
    currentPage: storedCurrentPage,
    perPage: storedPerPage,
    setFilters,
    setSelectedFilters,
    setSortedColumn,
    setCurrentPage: setStoredCurrentPage,
    setPerPage: setStoredPerPage,
    type,
  } = useContext(DashboardStoreContext)
  const {
    isDealerUser,
    isDealerService,
    isSelfManagedUser,
    isWpcUser,
    isWpcExecutive,
    isImpersonating,
    hasAcceptedTermsAndConditions,
    getUpdatedUser,
    user,
    setCurrentUser,
  } = useContext(UserStoreContext)
  const { createNewTask } = useContext(TaskStoreContext)
  const { clearStore } = useContext(RootStoreContext)
  // State
  const [loading, setLoading] = useState(true)
  const [loadingFilters, setLoadingFilters] = useState(true)
  const [dealers, setDealers] = useState([])
  const [kpis, setKpis] = useState(null)
  const [showTermsAndConditions, setShowTermsAndConditions] = useState()
  const [search, setSearch] = useState(null)
  const [searchTerm, setSearchTerm] = useState(null)
  const [loadingExport, setLoadingExport] = useState(false)

  // Pagination — init from store
  const [currentPage, setCurrentPage] = useState(storedCurrentPage)
  const [totalRows, setTotalRows] = useState()
  const [perPage, setPerPage] = useState(storedPerPage)
  const [pages, setPages] = useState(null)

  // Ref
  const isDashboardInitialMount = useRef(true)

  // Management type column visibility
  const [showManagementTypeColumn, setShowManagementTypeColumn] = useState(false)

  // Update hovered state of dealer name
  const { hoveredItemId, handleMouseEnter, handleMouseLeave } = useHoverItem()

  // Refs
  const searchInputRef = useRef(null)

  const handleErrors = (m) => toast(m, 'error')
  const handleSuccess = (message) => toast(message, 'success')

  // Cancels superseded list/KPI/filter requests so the last filter/page
  // *requested* wins, not the last to *resolve* (filter/search/pagination races).
  const { getSignal } = useAbortControllers()

  useEffect(() => {
    if (!location.pathname.includes('dashboard')) navigate('/dashboard', { replace: true })
  }, [])

  useEffect(() => {
    // WARP-567 — never prompt while viewing as someone else. Accepting is a write the
    // impersonator must not make, and agreeing to terms on another person's behalf is
    // meaningless anyway. The prompt is also non-dismissable and covers the whole
    // viewport, so showing it would hide the banner's Exit and strand the session.
    if (hasAcceptedTermsAndConditions === false && isDealerUser && !isImpersonating) {
      setShowTermsAndConditions(true)
    }
  }, [hasAcceptedTermsAndConditions, isImpersonating])

  // Sync pagination to store for persistence
  useEffect(() => {
    setStoredCurrentPage(currentPage)
  }, [currentPage])
  useEffect(() => {
    setStoredPerPage(perPage)
  }, [perPage])

  /**
   * Gets the updated list of dealers; updates pagination.
   * @param {string} url
   */
  const getUpdatedDealers = async (url) => {
    const response = await getDealers(url, () => {}, handleErrors, getSignal('dealers'))

    if (response) {
      setDealers(response.results)
      setTotalRows(response.count)
      setPages({ next: response.next, previous: response.previous })
      setShowManagementTypeColumn(
        response.meta?.showManagementTypeColumn === true && isDealerUser && type !== 'dealer',
      )
    }

    return response
  }

  /**
   * Gets the updated dealer KPIs.
   * @param {string} url
   */
  const getUpdatedDealerKpis = async (url) => {
    const response = await getDealerKpis(url, () => {}, handleErrors, getSignal('dealer-kpis'))

    if (response) {
      // Transform histogram into the format we need
      const histogram = _.map(response.roAgeHistogram, (value, key) => ({
        days: key,
        age: value,
      }))

      setKpis({
        ...response,
        roAgeHistogram: histogram,
      })
    }
  }

  /**
   * Configures the filter query, without the specified `without` value.
   * @param {string} without
   */
  const configureFilterQuery = (without = null) => {
    const filterQuery = {
      group: selectedFilters.dealerGroup,
      manufacturer: selectedFilters.manufacturer,
      admin: selectedFilters.admin,
      assistant_manager: selectedFilters.assistantManager,
      manager: selectedFilters.manager,
      dealer_management_system: selectedFilters.dealerManagementSystem,
      is_dealer_managed: selectedFilters.selfManaged,
    }

    if (without) delete filterQuery[without]

    return _.map(filterQuery, (value, key) => {
      if (value.length === 0) return null
      if (key === 'is_dealer_managed') {
        // Handle boolean filter for self-managed
        return `${key}=${_.map(value, 'id').join(`&${key}=`)}`
      }
      if (key === 'group') return `${key}=${_.map(value, 'id').join('&group=')}`
      return `${key}=${_.map(value, 'id').join(`&${key}=`)}`
    })
      .filter((x) => x !== null)
      .join('&')
  }

  useEffect(() => {
    const getUpdatedData = async () => {
      setLoading(true)
      setLoadingFilters(true)

      // If selfManaged filter is not selected, use the type variable to determine dealer type
      const includeType = type === 'wpc' ? 'is_dealer_managed=false' : 'is_dealer_managed=true'
      const typeOfDealers =
        isWpcExecutive && selectedFilters.selfManaged.length === 0 ? `&${includeType}` : ''

      const dealerFilters = configureFilterQuery()
      const configureFilterOption = (values, isObject = false) =>
        _.orderBy(
          _.map(values, (o) => (isObject ? { id: o.id, label: o.name } : { id: o, label: o })),
          'label',
        )

      // Load all filter base options
      const loadTeamFilters = type === 'wpc' && !isSelfManagedUser
      Promise.all([
        getFilterListOptions(
          `${DEALER_GROUP_BASE_URL}?${typeOfDealers}${configureFilterQuery('group')}`,
          () => {},
          () => {},
          getSignal('filter-group'),
        ),
        getFilterListOptions(
          `${MANUFACTURER_BASE_URL}?${typeOfDealers}${configureFilterQuery('manufacturer')}`,
          () => {},
          () => {},
          getSignal('filter-manufacturer'),
        ),
        loadTeamFilters &&
          getFilterListOptions(
            `${ADMIN_BASE_URL}?${typeOfDealers}${configureFilterQuery('admin')}`,
            () => {},
            () => {},
            getSignal('filter-admin'),
          ),
        loadTeamFilters &&
          getFilterListOptions(
            `${ASSISTANT_BASE_URL}?${typeOfDealers}${configureFilterQuery('assistant_manager')}`,
            () => {},
            () => {},
            getSignal('filter-assistant'),
          ),
        loadTeamFilters &&
          getFilterListOptions(
            `${MANAGER_BASE_URL}?${typeOfDealers}${configureFilterQuery('manager')}`,
            () => {},
            () => {},
            getSignal('filter-manager'),
          ),
        loadTeamFilters &&
          isDmsEnabled &&
          getFilterListOptions(
            `${DMS_BASE_URL}?${typeOfDealers}${configureFilterQuery('dealer_management_system')}`,
            () => {},
            () => {},
            getSignal('filter-dms'),
          ),
      ]).then((values) => {
        setFilters({
          dealerGroup: configureFilterOption(values[0], true),
          manufacturer: configureFilterOption(values[1], true),
          admin: loadTeamFilters ? configureFilterOption(values[2]) : [],
          assistantManager: loadTeamFilters ? configureFilterOption(values[3]) : [],
          manager: loadTeamFilters ? configureFilterOption(values[4]) : [],
          dealerManagementSystem:
            loadTeamFilters && isDmsEnabled ? configureFilterOption(values[5]) : [],
          selfManaged: isDealerUser
            ? [
                { id: 'true', label: 'Self-Managed' },
                { id: 'false', label: 'WPC Managed' },
              ]
            : [],
        })
        setLoadingFilters(false)
      })

      // Configure dealer filter and search
      let filterSearch = ''
      if (dealerFilters && search) {
        filterSearch = `&${dealerFilters}&q=${search}`
      } else if (dealerFilters) {
        filterSearch = `&${dealerFilters}`
      } else if (search) {
        filterSearch = `&q=${search}`
      }

      // Load the updated data
      const page = isDashboardInitialMount.current ? currentPage : 1
      if (!isDashboardInitialMount.current) setCurrentPage(1)
      isDashboardInitialMount.current = false
      Promise.all([
        getUpdatedDealers(
          `${DEALER_BASE_URL}${typeOfDealers}&limit=${perPage}&page=${page}&order_by=${sortedColumn}${filterSearch}`,
        ),
        getUpdatedDealerKpis(`${KPI_BASE_URL}?${typeOfDealers}${filterSearch}`),
      ]).then(([dealerResponse]) => {
        setLoading(false)
        if (search && dealerResponse) {
          track(SEARCH_PERFORMED, { context: 'dashboard', has_results: dealerResponse.count > 0 })
        }
      })
    }

    getUpdatedData()
  }, [type, search, selectedFilters, perPage, sortedColumn])

  /**
   * Debounces the search input.
   */
  const searchDealers = useCallback(
    _.debounce((term) => {
      setSearch(term)
    }, 500),
    [],
  )

  const columns = useMemo(
    () => [
      {
        cell: (row) => (
          <DealerNameCell
            row={row}
            hoveredRowId={hoveredItemId}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          />
        ),
        id: 'dealerName',
        minWidth: '250px',
        name: 'Dealer Name',
        selector: (row) => row.name,
        sortable: true,
        sortBy: 'name',
      },
      {
        cell: (row) => (
          <div className="bg-blue-light w-fit rounded-full px-3 py-1 text-xs font-semibold uppercase leading-tight text-blue-950">
            {row.isDealerManaged ? 'Self-Managed' : 'WPC Managed'}
          </div>
        ),
        id: 'managementType',
        grow: 0.75,
        minWidth: '150px',
        name: 'Type',
        selector: (row) => (row.isDealerManaged ? 'Self-Managed' : 'WPC Managed'),
        sortable: true,
        sortBy: 'is_dealer_managed',
        omit: !showManagementTypeColumn,
      },
      {
        grow: 0.25,
        id: 'numberOfOpenRos',
        name: 'ROs',
        right: true,
        selector: (row) => row.roMetrics?.numberOfOpenRos,
        sortable: true,
        sortBy: 'number_of_open_ros',
      },
      {
        grow: 0.5,
        id: 'totalUnresolvedClaimAmount',
        cell: (row) => (
          <span>
            {Number(row.roMetrics?.totalUnresolvedClaimAmount).toLocaleString('en-US', {
              style: 'currency',
              currency: 'USD',
              maximumFractionDigits: 0,
              minimumFractionDigits: 0,
            })}
          </span>
        ),
        name: 'Total',
        right: true,
        selector: (row) => row.roMetrics?.totalUnresolvedClaimAmount,
        sortable: true,
        sortBy: 'total_unresolved_claim_amount',
      },
      {
        id: 'dealerGroup',
        minWidth: '200px',
        name: 'Dealer Group',
        selector: (row) => row.group?.name || 'N/A',
        cell: (row) => <span>{row.group?.name || 'N/A'}</span>,
        sortable: true,
        sortBy: 'group__name',
        omit: isDealerUser,
      },
      {
        id: 'mfg',
        grow: 0.75,
        name: 'MFG',
        selector: (row) => row.manufacturer?.name || 'N/A',
        sortable: true,
        sortBy: 'manufacturer__name',
      },
      {
        cell: (row) => (
          <div className="flex flex-row gap-1">
            <img src={UserIcon} className="mr-1" alt="User" /> <span>{row.admin || 'N/A'}</span>
          </div>
        ),
        id: 'admin',
        mindWidth: '200px',
        name: 'Admin',
        selector: (row) => row.admin,
        sortable: true,
        sortBy: 'admin',
        omit: type === 'dealer' || isSelfManagedUser,
      },
      {
        cell: (row) => (
          <div className="flex flex-row gap-1">
            <img src={UserIcon} className="mr-1" alt="User" />{' '}
            <span>{row.assistantManager || 'N/A'}</span>
          </div>
        ),
        id: 'assistantManager',
        mindWidth: '200px',
        name: 'Assistant Manager',
        selector: (row) => row.assistantManager,
        sortable: true,
        sortBy: 'assistant_manager',
        omit: type === 'dealer' || isSelfManagedUser,
      },
      {
        cell: (row) => (
          <div className="flex flex-row gap-1">
            <img src={UserIcon} className="mr-1" alt="User" /> <span>{row.manager || 'N/A'}</span>
          </div>
        ),
        id: 'manager',
        mindWidth: '200px',
        name: 'Manager',
        selector: (row) => row.manager,
        sortable: true,
        sortBy: 'manager',
        omit: type === 'dealer' || isSelfManagedUser,
      },
      {
        id: 'dealerManagementSystem',
        minWidth: '150px',
        name: 'DMS',
        selector: (row) => row.dealerManagementSystem || 'N/A',
        sortable: true,
        sortBy: 'dealer_management_system',
        omit: !isDmsEnabled || type === 'dealer' || isSelfManagedUser,
      },
    ],
    [showManagementTypeColumn, isDealerUser, type, isSelfManagedUser, hoveredItemId],
  )

  return (
    <div className="flex flex-col">
      <Helmet>
        <title>Dealers</title>
      </Helmet>

      <h5>Highlights</h5>

      <Highlights kpis={kpis} loading={loading} />

      <div className="my-4 flex flex-col">
        <div className="flex w-full flex-row items-center justify-between">
          <span className="text-charcoal-900 text-2xl font-semibold leading-8 tracking-[-1px]">
            Dealers
          </span>

          <Button
            className="px-1 py-2"
            dataTestId="export-dealers"
            disabled={loadingExport || isImpersonating}
            label={<DownloadIcon className="h-4 stroke-[#ffffff]" />}
            ariaLabel="Export Dealers"
            onClick={async () => {
              // track export initiation
              track(EXPORT_INITIATED, { type: 'dealer' })

              // If selfManaged filter is not selected, use the type variable to determine dealer type
              const includeType =
                type === 'wpc' ? 'is_dealer_managed=false' : 'is_dealer_managed=true'

              // Only include dealer type in export if selfManaged filter is not selected and user is WPC executive
              const typeOfDealers =
                isWpcExecutive && selectedFilters.selfManaged.length === 0 ? `&${includeType}` : ''

              const query = configureFilterQuery()
              const params = search ? `q=${search}&${query}` : `${query}`
              const exportTask = await createDealerExport(
                `/dealer-exports/?${params}${typeOfDealers}`,
                handleErrors,
                setLoadingExport,
              )

              if (exportTask) {
                createNewTask('Download', exportTask, {
                  label: 'Dealers',
                  filename: `Dealers_${new Date().toISOString().slice(0, 10)}.xlsx`,
                  checkUpdatedTask: async () => {
                    const updatedTask = await getDealerExport(exportTask.id, handleErrors)
                    return updatedTask
                  },
                })
              }
            }}
          />
        </div>

        <div className="flex flex-col gap-5 py-3 lg:flex-row">
          <TextInput
            className="w-full pl-10 md:w-72"
            containerClassName="w-full md:w-72"
            data-testid="search"
            icon={<SearchIcon className="stroke-charcoal ml-2 w-5" />}
            endIcon={
              searchTerm ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm('')
                    searchDealers(null)
                    searchInputRef.current.value = ''
                  }}
                >
                  <XMarkIcon className="text-midnight h-5" aria-hidden="true" />
                </button>
              ) : null
            }
            onChange={(e) => {
              setSearchTerm(e.target.value)
              searchDealers(e.target.value)
            }}
            placeholder="Search"
            value={searchTerm}
            ref={searchInputRef}
          />

          <div className="flex w-full flex-col flex-wrap items-center justify-end gap-2 md:flex-row">
            {_.values(selectedFilters).some((o) => o.length > 0) && (
              <button
                className="mr-3 cursor-pointer rounded-md text-sm font-semibold leading-5 tracking-[0.25px] text-blue-800 hover:text-blue-900"
                onClick={() => resetFilters()}
                type="button"
              >
                Reset Filters
              </button>
            )}
            {isWpcUser && (
              <MultiSelectDropdown
                className="w-full md:max-w-[250px]"
                containerClassName="w-full md:w-auto"
                disabled={loadingFilters}
                id="dealer_group-filter"
                onChange={(o) => setSelectedFilters({ ...selectedFilters, dealerGroup: o })}
                options={filters.dealerGroup}
                placeholder="Select a Dealer Group"
                search
                value={selectedFilters.dealerGroup}
              />
            )}
            <MultiSelectDropdown
              className="w-full md:max-w-[250px]"
              containerClassName="w-full md:w-auto"
              disabled={loadingFilters}
              id="manufacturer-filter"
              onChange={(o) => setSelectedFilters({ ...selectedFilters, manufacturer: o })}
              options={filters.manufacturer}
              placeholder="Select a Manufacturer"
              search
              value={selectedFilters.manufacturer}
            />

            {showManagementTypeColumn && (
              <MultiSelectDropdown
                className="w-full md:max-w-[250px]"
                containerClassName="w-full md:w-auto"
                disabled={loadingFilters}
                id="self-managed-filter"
                onChange={(o) => setSelectedFilters({ ...selectedFilters, selfManaged: o })}
                options={filters.selfManaged}
                placeholder="Select Management Type"
                singleSelect
                value={selectedFilters.selfManaged}
              />
            )}

            {type === 'wpc' && !isSelfManagedUser && (
              <>
                <MultiSelectDropdown
                  className="w-full md:max-w-[250px]"
                  containerClassName="w-full md:w-auto"
                  disabled={loadingFilters}
                  id="admin-filter"
                  onChange={(o) => setSelectedFilters({ ...selectedFilters, admin: o })}
                  options={filters.admin}
                  placeholder="Select an Admin"
                  search
                  value={selectedFilters.admin}
                />

                <MultiSelectDropdown
                  className="w-full md:max-w-[250px]"
                  containerClassName="w-full md:w-auto"
                  disabled={loadingFilters}
                  id="assistant_manager-filter"
                  onChange={(o) => setSelectedFilters({ ...selectedFilters, assistantManager: o })}
                  options={filters.assistantManager}
                  placeholder="Select an Assistant Manager"
                  search
                  value={selectedFilters.assistantManager}
                />

                <MultiSelectDropdown
                  className="w-full md:max-w-[250px]"
                  containerClassName="w-full md:w-auto"
                  disabled={loadingFilters}
                  id="manager-filter"
                  onChange={(o) => setSelectedFilters({ ...selectedFilters, manager: o })}
                  options={filters.manager}
                  placeholder="Select a Manager"
                  search
                  value={selectedFilters.manager}
                />

                {isDmsEnabled && (
                  <MultiSelectDropdown
                    className="w-full md:max-w-[250px]"
                    containerClassName="w-full md:w-auto"
                    disabled={loadingFilters}
                    id="dms-filter"
                    onChange={(o) =>
                      setSelectedFilters({ ...selectedFilters, dealerManagementSystem: o })
                    }
                    options={filters.dealerManagementSystem}
                    placeholder="Select a DMS"
                    search
                    value={selectedFilters.dealerManagementSystem}
                  />
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <div className="grid">
        <DataTable
          onRowClicked={(row) => navigate(`/dealer/${row.id}/detail`, { state: { dealer: row } })}
          highlightOnHover
          columns={columns}
          data={dealers}
          {...getSortProps(sortedColumn, columns)}
          onChangePage={(page) => {
            const dealerFilters = configureFilterQuery()
            handlePagination(
              page,
              currentPage,
              perPage,
              totalRows,
              pages,
              setCurrentPage,
              getUpdatedDealers,
              `${DEALER_BASE_URL}&limit=`,
              `order_by=${sortedColumn}${dealerFilters ? `&${dealerFilters}` : ''}`,
            )
          }}
          onChangeRowsPerPage={(currentRowsPerPage) => setPerPage(currentRowsPerPage)}
          onSort={(column, direction) => {
            track(SORT_CHANGED, { context: 'dashboard', column: column.sortBy, direction })
            // Reverse sort direction for managementType column
            let d = direction === 'asc' ? '' : '-'
            if (column.id === 'managementType') {
              d = direction === 'asc' ? '-' : ''
            }
            setSortedColumn(`${d}${column.sortBy}`)
          }}
          pagination
          paginationServer
          paginationDefaultPage={currentPage}
          paginationPerPage={perPage}
          paginationRowsPerPageOptions={[5, 10, 20, 30, 50]}
          paginationTotalRows={totalRows}
          progressPending={loading}
          sortServer
        />
      </div>

      {showTermsAndConditions && (
        <Modal
          open
          title="Terms and Conditions"
          onClose={() => {}}
          setOpen={() => {}}
          description="You must agree to the terms and conditions to access the site."
          content={
            <div className="relative my-4 h-[300px] overflow-y-scroll px-2">
              <p className="text-md">
                {isDealerService
                  ? 'By registering and using the WARP Platform, You acknowledge and agree that the queries, questions, design, layout, and organization that are made and presented to You by the WARP Platform as well as the compilation of information that is input by You into the WARP Platform (collectively, “Compilations”) are proprietary, confidential, and trade secret of Warranty Processing Company, Inc. You agree not to distribute or otherwise disclose the Compilations to another party or another person, and You agree that any disclosure of the Compilations to another party or another person is prohibited and shall cause irreparable harm to Warranty Processing Company, Inc.'
                  : 'You hereby represent that you have authority on behalf of Dealer and, by Dealer registering and using the WARP Platform, You acknowledge and agree that the queries, questions, design, layout, and organization that are made and presented to Dealer and its users by the WARP Platform as well as the compilation of information that is input by Dealer into the WARP Platform (collectively, “Compilations”) are proprietary, confidential, and trade secret of Warranty Processing Company, Inc. You and Dealer agree not to distribute or otherwise disclose the Compilations to another party or another person, and You and Dealer agree that any disclosure of the Compilations to another party or another person is prohibited and shall cause irreparable harm to Warranty Processing Company, Inc.'}
              </p>
              <p className="text-md mt-2 font-bold">SMS Messaging for MFA</p>
              <ol>
                <li className="list-inside list-decimal">
                  You can opt-in to SMS messaging for MFA verification codes in your Profile Page.
                </li>
                <li className="list-inside list-decimal">
                  You can cancel the SMS service at any time. Just text &quot;STOP&quot; to the
                  short code. After you send the SMS message &quot;STOP&quot; to us, we will send
                  you an SMS message to confirm that you have been unsubscribed. After this, you
                  will no longer receive SMS messages from us. If you want to join again, just sign
                  up as you did the first time and we will start sending SMS messages to you again.
                </li>
                <li className="list-inside list-decimal">
                  If you are experiencing issues with the messaging program you can reply with the
                  keyword HELP for more assistance.
                </li>
                <li className="list-inside list-decimal">
                  Carriers are not liable for delayed or undelivered messages.
                </li>
                <li className="list-inside list-decimal">
                  As always, message and data rates may apply for any messages sent to you from us
                  and to us from you. If you have any questions about your text plan or data plan,
                  it is best to contact your wireless provider.
                </li>
                <li className="list-inside list-decimal">
                  If you have any questions regarding privacy, please read our{' '}
                  <Link className="underline" to="/privacy-policy" target="_blank">
                    privacy policy
                  </Link>
                  .
                </li>
              </ol>
              <div className="from-white-dark pointer-events-none sticky bottom-0 left-0 h-[50px] w-full bg-gradient-to-t to-transparent"></div>
            </div>
          }
          actions={[
            {
              type: 'cancel',
              label: 'Logout',
              onClick: () => logout(user.refreshToken, clearStore),
            },
            {
              type: 'submit',
              label: 'I Agree',
              onClick: async () => {
                await updateUser(
                  { id: user.id, acceptedTermsAndConditionsAt: new Date().toISOString() },
                  handleErrors,
                  () => {},
                  async () => {
                    handleSuccess('Terms and Conditions accepted.')
                    setShowTermsAndConditions(false)

                    await getUpdatedUser(user.id)
                  },
                  setCurrentUser,
                )
              },
            },
          ]}
        />
      )}
    </div>
  )
})

export default Dashboard
